import { NumberInput, Paper, Stack, Textarea } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { receiptApi } from "apis/receiptApi";
import { Loading } from "components";
import { ErrorStatusType } from "constants/types/common";
import { FormikProvider, useFormik } from "formik";
import { FC, useState } from "react";
import { useNotification } from "utils/hooks";
import { useListBankAccount } from "utils/hooks/request/useBankAccount";
import BankAccountFields from "../BankAccountFields/BankAccountFields";
import {
  PaymentMethod,
  ReceiptFormValues,
  initialValues,
  toCreateBody,
  validationSchema,
} from "./config";

interface ReceiptCreateFormProps {
  saleOrderId: string;
  maxAmount: number;
  formId: string;
  onClose?: () => void;
  mutate?: () => void;
}

const ReceiptCreateForm: FC<ReceiptCreateFormProps> = ({
  saleOrderId,
  maxAmount,
  formId,
  onClose,
  mutate,
}) => {
  const { notification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("bank");
  const {
    bankAccountList,
    bankAccountOptions,
    isLoading: isLoadingBankAccounts,
  } = useListBankAccount();

  const formik = useFormik<ReceiptFormValues>({
    initialValues: initialValues(maxAmount),
    validationSchema: validationSchema(maxAmount, paymentMethod),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const res = await receiptApi.create(toCreateBody(values, saleOrderId, paymentMethod));
        if (res.data.success) {
          notification.success({ title: "Tạo phiếu thu thành công" });
          onClose?.();
          mutate?.();
          formik.resetForm();
        }
      } catch (error: ErrorStatusType | any) {
        notification.error({
          title: "Tạo phiếu thu thất bại",
          message: error?.error?.message,
        });
      }
      setIsLoading(false);
    },
  });

  return (
    <FormikProvider value={formik}>
      <Loading variant="overlay" visible={isLoading}>
        <Paper>
          <form id={formId} onSubmit={formik.handleSubmit}>
            <Stack gap="md">
              <BankAccountFields
                paymentMethod={paymentMethod}
                onPaymentMethodChange={setPaymentMethod}
                value={formik.values.bankAccount}
                onChange={(next) => formik.setFieldValue("bankAccount", next)}
                onFieldBlur={(field) =>
                  formik.setFieldTouched(`bankAccount.${field}`, true)
                }
                errors={
                  typeof formik.errors.bankAccount === "object"
                    ? formik.errors.bankAccount
                    : undefined
                }
                touched={
                  typeof formik.touched.bankAccount === "object"
                    ? formik.touched.bankAccount
                    : undefined
                }
                bankAccountList={bankAccountList}
                bankAccountOptions={bankAccountOptions}
                isLoadingBankAccounts={isLoadingBankAccounts}
              />
              <NumberInput
                label="Số tiền thu"
                placeholder="Nhập số tiền"
                description={`Tối đa: ${maxAmount.toLocaleString("vi-VN")} đ`}
                withAsterisk
                min={0}
                max={maxAmount}
                thousandSeparator="."
                decimalSeparator=","
                suffix=" đ"
                value={formik.values.amount}
                onChange={(v) => formik.setFieldValue("amount", v)}
                onBlur={() => formik.setFieldTouched("amount", true)}
                error={
                  formik.touched.amount && formik.errors.amount
                    ? formik.errors.amount
                    : null
                }
              />

              <DateTimePicker
                label="Ngày thu"
                placeholder="Chọn ngày thu"
                withAsterisk
                value={formik.values.paymentDate}
                onChange={(v) => formik.setFieldValue("paymentDate", v)}
                onBlur={() => formik.setFieldTouched("paymentDate", true)}
                error={
                  formik.touched.paymentDate && formik.errors.paymentDate
                    ? String(formik.errors.paymentDate)
                    : null
                }
              />

              <Textarea
                label="Ghi chú"
                placeholder="Nhập ghi chú (tuỳ chọn)"
                value={formik.values.notes}
                onChange={(e) => formik.setFieldValue("notes", e.target.value)}
                rows={3}
              />
            </Stack>
          </form>
        </Paper>
      </Loading>
    </FormikProvider>
  );
};

export default ReceiptCreateForm;
